import {MouseEvent, ReactNode} from "react";
import {css, Theme} from "@emotion/react";

import {Container} from "@pg-design/grid-module";
import {calculateRemSize, flexAbsoluteCenter, mb, onDesktop, ph, positionRelative, pv, w100} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {RequestState} from "@pg-mono/request-state";

import {PropertyBoxVariantBCD} from "../atoms/PropertyBox/PropertyBoxVariantBCD";
import {usePrimaryClick} from "../hooks/use_primary_click";
import {createOfferListingAmplitudePendingDetailUrl} from "../offer/list/helpers/offer_listing_amplitude_item_list_id";
import {IOfferDetail} from "../offer/types/IOfferDetail";
import {IPropertyListProperty} from "../property/types/PropertyListProperty";
import {getAmplitudeItemListContextWithStaticFallback} from "../tracking/amplitude/get_amplitude_item_list_context_with_static_fallback";
import {IAmplitudeItemListContext, IAmplitudeViewItemOffer} from "../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackPropertySelectItemWithListContext} from "../tracking/amplitude/track_property_select_item_with_list_context";
import {gtmEventPropertyListClick} from "../tracking/google_tag_manager/gtm_event_property_list";
import {useGTMEcommercePropertyListViewTracking} from "../tracking/hooks/use_gtm_ecommerce_property_list_view_tracking";
import {PropertyListLoader} from "./PropertyListLoader";

export interface IPropertyTileListProps {
    offer: IOfferDetail;
    properties: IPropertyListProperty[];
    requestState: RequestState;
    className?: string;
    gridLayoutCols?: {
        sm?: number;
        md?: number;
        lg?: number;
    };
    onPropertyTileClick?: (property: IPropertyListProperty) => void;
    propertyBoxComponent?: "div";
    renderPropertyTile?: (property: IPropertyListProperty) => ReactNode;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    trackingMeta: {
        listId: string;
        listName: string;
    };
}

export const PropertyListTileList = (props: IPropertyTileListProps) => {
    const {
        offer,
        properties,
        requestState,
        className,
        gridLayoutCols,
        onPropertyTileClick,
        propertyBoxComponent,
        renderPropertyTile,
        amplitudeItemListContext,
        trackingMeta
    } = props;

    const {containerId} = useGTMEcommercePropertyListViewTracking({
        offer,
        properties,
        listId: trackingMeta.listId,
        listName: trackingMeta.listName,
        useVisibilityTracking: true,
        isListReady: requestState === RequestState.Success
    });
    const amplitudeSelectItemListContext = getAmplitudeItemListContextWithStaticFallback({
        dynamicItemListContext: amplitudeItemListContext,
        trackingMeta
    });
    const updatePropertyDetailLinkWithPendingContext = (event: MouseEvent, property: IPropertyListProperty) => {
        const anchor = (event.target as HTMLElement).closest("a[href]") as HTMLAnchorElement | null;

        if (!anchor) {
            return;
        }

        anchor.href = createOfferListingAmplitudePendingDetailUrl({
            url: anchor.href,
            itemId: offer.id,
            propertyId: property.id,
            itemListContext: amplitudeItemListContext
        });
    };

    const handlePropertyTileClick = usePrimaryClick((event: MouseEvent, property: IPropertyListProperty, index: number) => {
        gtmEventPropertyListClick("rzut");
        trackPropertySelectItemWithListContext({
            offer: {
                id: offer.id,
                name: offer.name,
                type: offer.type,
                currency: offer.currency,
                vendor: offer.vendor,
                region: offer.region as IAmplitudeViewItemOffer["region"]
            },
            property,
            index,
            itemListContext: amplitudeSelectItemListContext
        });

        updatePropertyDetailLinkWithPendingContext(event, property);
        onPropertyTileClick?.(property);
    });

    if (properties.length > 0) {
        return (
            <Container css={[pv(0), flexAbsoluteCenter, positionRelative]} className={className} data-id={containerId}>
                <ul css={[propertyListPlanBoxList, gridColumns(gridLayoutCols)]}>
                    {properties.map((property, index) => {
                        return (
                            <li key={property.id} onMouseDown={(event) => handlePropertyTileClick(event, property, index)}>
                                <div css={[tileListItemStyle, flexAbsoluteCenter, w100]}>
                                    {renderPropertyTile ? (
                                        renderPropertyTile(property)
                                    ) : (
                                        <PropertyBoxVariantBCD property={property} activeRedirectButton as={propertyBoxComponent} />
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>

                {requestState === RequestState.Waiting && <PropertyListLoader />}
            </Container>
        );
    } else if (requestState === RequestState.Waiting) {
        return (
            <div css={loaderWrapNoProperties}>
                <Loader size="md" />
            </div>
        );
    } else {
        return <div css={noOffersText}>Brak ofert do wyświetlenia</div>;
    }
};

const propertyListPlanBoxList = (theme: Theme) => css`
    list-style: none;
    display: grid;
    column-gap: ${calculateRemSize(1.5)};
    row-gap: ${calculateRemSize(1.5)};
    ${mb(3)};
    ${ph(2)};

    @media (min-width: ${theme.breakpoints.sm}) {
        ${ph(5)};
    }

    ${onDesktop(css`
        column-gap: ${calculateRemSize(2)};
        row-gap: ${calculateRemSize(2)};
    `)};
`;

const gridColumns = (gridLayoutCols?: IPropertyTileListProps["gridLayoutCols"]) => (theme: Theme) => css`
    grid-template-columns: 1fr;

    @media (min-width: ${theme.breakpoints.sm}) {
        grid-template-columns: repeat(${gridLayoutCols?.sm || 2}, 1fr);
    }

    ${onDesktop(css`
        grid-template-columns: repeat(${gridLayoutCols?.md || 3}, 1fr);
    `)};

    @media (min-width: ${theme.breakpoints.lg}) {
        grid-template-columns: repeat(${gridLayoutCols?.lg || 4}, 1fr);
    }
`;

const tileListItemStyle = css`
    position: relative;
    height: 100%;
    display: flex;
    justify-content: center;
`;

const loaderWrapNoProperties = css`
    max-height: 100%;
    max-width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const noOffersText = css`
    font-size: 1.6rem;
    line-height: 2.4rem;
    padding: 1.4rem;
`;
import {MouseEvent, ReactNode} from "react";
import {css, Theme} from "@emotion/react";

import {Container} from "@pg-design/grid-module";
import {calculateRemSize, flexAbsoluteCenter, mb, onDesktop, ph, positionRelative, pv, w100} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {RequestState} from "@pg-mono/request-state";

import {PropertyBoxVariantBCD} from "../atoms/PropertyBox/PropertyBoxVariantBCD";
import {usePrimaryClick} from "../hooks/use_primary_click";
import {createOfferListingAmplitudePendingDetailUrl} from "../offer/list/helpers/offer_listing_amplitude_item_list_id";
import {IOfferDetail} from "../offer/types/IOfferDetail";
import {IPropertyListProperty} from "../property/types/PropertyListProperty";
import {getAmplitudeItemListContextWithStaticFallback} from "../tracking/amplitude/get_amplitude_item_list_context_with_static_fallback";
import {IAmplitudeItemListContext, IAmplitudeViewItemOffer} from "../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackPropertySelectItemWithListContext} from "../tracking/amplitude/track_property_select_item_with_list_context";
import {gtmEventPropertyListClick} from "../tracking/google_tag_manager/gtm_event_property_list";
import {useGTMEcommercePropertyListViewTracking} from "../tracking/hooks/use_gtm_ecommerce_property_list_view_tracking";
import {PropertyListLoader} from "./PropertyListLoader";

export interface IPropertyTileListProps {
    offer: IOfferDetail;
    properties: IPropertyListProperty[];
    requestState: RequestState;
    className?: string;
    gridLayoutCols?: {
        sm?: number;
        md?: number;
        lg?: number;
    };
    onPropertyTileClick?: (property: IPropertyListProperty) => void;
    propertyBoxComponent?: "div";
    renderPropertyTile?: (property: IPropertyListProperty) => ReactNode;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    trackingMeta: {
        listId: string;
        listName: string;
    };
}

export const PropertyListTileList = (props: IPropertyTileListProps) => {
    const {
        offer,
        properties,
        requestState,
        className,
        gridLayoutCols,
        onPropertyTileClick,
        propertyBoxComponent,
        renderPropertyTile,
        amplitudeItemListContext,
        trackingMeta
    } = props;

    const {containerId} = useGTMEcommercePropertyListViewTracking({
        offer,
        properties,
        listId: trackingMeta.listId,
        listName: trackingMeta.listName,
        useVisibilityTracking: true,
        isListReady: requestState === RequestState.Success
    });
    const amplitudeSelectItemListContext = getAmplitudeItemListContextWithStaticFallback({
        dynamicItemListContext: amplitudeItemListContext,
        trackingMeta
    });
    const updatePropertyDetailLinkWithPendingContext = (event: MouseEvent, property: IPropertyListProperty) => {
        const anchor = (event.target as HTMLElement).closest("a[href]") as HTMLAnchorElement | null;

        if (!anchor) {
            return;
        }

        anchor.href = createOfferListingAmplitudePendingDetailUrl({
            url: anchor.href,
            itemId: offer.id,
            propertyId: property.id,
            itemListContext: amplitudeItemListContext
        });
    };

    const handlePropertyTileClick = usePrimaryClick((event: MouseEvent, property: IPropertyListProperty, index: number) => {
        gtmEventPropertyListClick("rzut");
        trackPropertySelectItemWithListContext({
            offer: {
                id: offer.id,
                name: offer.name,
                type: offer.type,
                currency: offer.currency,
                vendor: offer.vendor,
                region: offer.region as IAmplitudeViewItemOffer["region"]
            },
            property,
            index,
            itemListContext: amplitudeSelectItemListContext
        });

        updatePropertyDetailLinkWithPendingContext(event, property);
        onPropertyTileClick?.(property);
    });

    if (properties.length > 0) {
        return (
            <Container css={[pv(0), flexAbsoluteCenter, positionRelative]} className={className} data-id={containerId}>
                <ul css={[propertyListPlanBoxList, gridColumns(gridLayoutCols)]}>
                    {properties.map((property, index) => {
                        return (
                            <li key={property.id} onMouseDown={(event) => handlePropertyTileClick(event, property, index)}>
                                <div css={[tileListItemStyle, flexAbsoluteCenter, w100]}>
                                    {renderPropertyTile ? (
                                        renderPropertyTile(property)
                                    ) : (
                                        <PropertyBoxVariantBCD property={property} activeRedirectButton as={propertyBoxComponent} />
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>

                {requestState === RequestState.Waiting && <PropertyListLoader />}
            </Container>
        );
    } else if (requestState === RequestState.Waiting) {
        return (
            <div css={loaderWrapNoProperties}>
                <Loader size="md" />
            </div>
        );
    } else {
        return <div css={noOffersText}>Brak ofert do wyświetlenia</div>;
    }
};

const propertyListPlanBoxList = (theme: Theme) => css`
    list-style: none;
    display: grid;
    column-gap: ${calculateRemSize(1.5)};
    row-gap: ${calculateRemSize(1.5)};
    ${mb(3)};
    ${ph(2)};

    @media (min-width: ${theme.breakpoints.sm}) {
        ${ph(5)};
    }

    ${onDesktop(css`
        column-gap: ${calculateRemSize(2)};
        row-gap: ${calculateRemSize(2)};
    `)};
`;

const gridColumns = (gridLayoutCols?: IPropertyTileListProps["gridLayoutCols"]) => (theme: Theme) => css`
    grid-template-columns: 1fr;

    @media (min-width: ${theme.breakpoints.sm}) {
        grid-template-columns: repeat(${gridLayoutCols?.sm || 2}, 1fr);
    }

    ${onDesktop(css`
        grid-template-columns: repeat(${gridLayoutCols?.md || 3}, 1fr);
    `)};

    @media (min-width: ${theme.breakpoints.lg}) {
        grid-template-columns: repeat(${gridLayoutCols?.lg || 4}, 1fr);
    }
`;

const tileListItemStyle = css`
    position: relative;
    height: 100%;
    display: flex;
    justify-content: center;
`;

const loaderWrapNoProperties = css`
    max-height: 100%;
    max-width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const noOffersText = css`
    font-size: 1.6rem;
    line-height: 2.4rem;
    padding: 1.4rem;
`;
